import {
  getAllInternalBaseApiUrls,
  getApiUrl,
} from "../components/utils/helper";

/**
 * Custom error interface that includes HTTP status code
 */
interface HttpError extends Error {
  statusCode: number;
}

/**
 * Authentication token manager implemented as an IIFE (Immediately Invoked Function Expression)
 * Intercepts fetch requests to add authentication headers and handle token refresh
 */
const authTokenManager = (() => {
  const originalFetch = window.fetch;

  const TOKEN_KEY = "_moTokens";
  const TOKEN_EXPIRY_BUFFER_SECONDS = 30; // Buffer time in seconds before token expiry
  const refreshTokenEndpoint = "/auth/refresh";
  // Token management helpers
  let isRefreshing = false;
  let requestWaitListReferences: {
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
  }[] = [];

  const authBypassUrls = [
    "/member/?step=registration",
    "/auth",
    // Add urls here that don't require authorisation
  ];

  /**
   * Overrides the global fetch function to handle authentication tokens
   * Adds authentication headers to internal API requests and handles token refresh when needed
   */
  window.fetch = async (
    input: string | Request | URL,
    init?: RequestInit
  ): Promise<Response> => {
    //If you need to disable auth token manager all together, uncomment the following line
    // that will return original request without any modification or a logic
    // return originalFetch(input, init);

    
    const requestUrl =
      typeof input === "string"
        ? input
        : input instanceof URL
        ? input.toString()
        : input.url;

    const internalApiUrls = getAllInternalBaseApiUrls();
    // Check if the request URL is for an internal API
    const isInternalApiRequest = internalApiUrls.some((baseUrl) =>
      requestUrl.startsWith(baseUrl)
    );
    // Skip token handling for token refresh endpoint to avoid infinite loops
    const isRefreshTokenEndpoint = requestUrl.includes(refreshTokenEndpoint);

    //Skip token handling for auth bypass urls or if request is not for internal API or token refresh endpoint
    if (
      !isInternalApiRequest ||
      isRefreshTokenEndpoint ||
      authBypassUrls.some((url) => requestUrl.includes(url))
    ) {
      return originalFetch(input, init);
    }

    //If API is internal, add auth header to request
    const tokenData = getTokenData();
    let idToken = tokenData?.idToken;

    //If token is available and expired, refresh token
    if (idToken && isTokenExpired(idToken)) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          //Get new token
          idToken = await refreshIdToken();
          //Notify all waiting requests with new token
          processWaitListRefreshRequests(idToken, null);
        } catch (error) {
          clearTokenData();
          processWaitListRefreshRequests(null, error);
          redirectLogin();
        }
        isRefreshing = false;
      }
    }

    if (isRefreshing) {
      // Wait for the refresh to complete and continue with request
      idToken = await handleWaitRequestForRefreshToken();
    }

    if (idToken) {
      init = {
        ...init,
        headers: {
          ...(init?.headers || {}),
          Authorization: getAuthHeader(idToken),
        },
      };
    }

    let response = await originalFetch(input, init);

    if (!response.ok && (response.status === 401 || response.status === 403)) {
      //if request is unauthorized or forbidden, grab new token and retry request
      const tokenData = getTokenData();
      const idToken = tokenData?.idToken;
      const refreshToken = tokenData?.refreshToken;

      if (!idToken || !refreshToken) {
        clearTokenData();
        processWaitListRefreshRequests(
          null,
          constructError("Invalid token", response.status)
        );
        redirectLogin();
        return response;
      }

      isRefreshing = true;
      let newIdToken = null;

      try {
        newIdToken = await refreshIdToken();
      } catch (error) {
        clearTokenData();
        processWaitListRefreshRequests(null, error);
        redirectLogin();
        return response;
      }

      init = {
        ...init,
        headers: {
          ...(init?.headers || {}),
          Authorization: getAuthHeader(newIdToken),
        },
      };
      response = await originalFetch(input, init);
      isRefreshing = false;
      processWaitListRefreshRequests(newIdToken, null);
    }
    return response;
  };

  /**
   * Retrieves authentication tokens from local storage
   * @returns Object containing idToken and refreshToken, or null if not found
   */
  const getTokenData = (): {
    idToken?: string;
    refreshToken?: string;
  } | null => {
    const tokenString = localStorage.getItem(TOKEN_KEY);
    return tokenString ? JSON.parse(tokenString) : null;
  };

  /**
   * Stores authentication tokens in local storage
   * Merges with existing token data if present
   * @param tokenObj Object containing idToken and/or refreshToken
   */
  const setTokenData = (tokenObj: {
    idToken?: string;
    refreshToken?: string;
  }) => {
    let tokenData = getTokenData();
    tokenData = { ...tokenData, ...tokenObj };
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenData));
  };

   /**
   * Removes all authentication tokens from local storage
   */
   const clearTokenData = () => {
    localStorage.removeItem(TOKEN_KEY);
  };

  /**
   * Decodes and parses a JWT token to access its payload
   * @param token JWT token string
   * @returns Parsed JWT payload as an object, or null if parsing fails
   */
  const parseJwt = (token: string) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error parsing JWT token:", error);
      return null;
    }
  };

  /**
   * Makes an API call to refresh the authentication token
   * @returns New ID token if refresh succeeds, null if tokens are missing
   * @throws HttpError if refresh request fails
   */
  const refreshIdToken = async () => {
    const tokenData = getTokenData();
    const idToken = tokenData?.idToken;
    const refreshToken = tokenData?.refreshToken;

    if (!idToken || !refreshToken) {
      return null;
    }

    const response = await originalFetch(getApiUrl(refreshTokenEndpoint), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      throw constructError(response.statusText, response.status);
    }

    const newTokens = await response.json();
    setTokenData(newTokens);
    return newTokens.idToken;
  };

  /**
   * Checks if a JWT token is expired or about to expire
   * Includes a buffer time before actual expiration
   * @param token JWT token to check
   * @returns true if token is missing, invalid, or expired/about to expire
   */
  const isTokenExpired = (token?: string): boolean => {
    if (!token) return true;

    const tokenPayload = parseJwt(token);
    if (!tokenPayload || !tokenPayload.exp) return true;

    const expiryTime = tokenPayload.exp;
    const now = Math.floor(Date.now() / 1000);

    return expiryTime <= now + TOKEN_EXPIRY_BUFFER_SECONDS;
  };

  /**
   * Formats a token for use in the Authorization header
   * @param token JWT token
   * @returns Formatted Authorization header value
   */
  const getAuthHeader = (token: string) => {
    return `Bearer ${token}`;
  };

  /**
   * Creates a promise that will resolve when a token refresh completes
   * Used to queue requests during token refresh
   * @returns Promise that resolves with new token or rejects with error
   */
  const handleWaitRequestForRefreshToken = () => {
    return new Promise(
      (
        resolveHandle: (token: string) => void,
        rejectHandle: (error: unknown) => void
      ) => {
        requestWaitListReferences.push({
          resolve: resolveHandle,
          reject: rejectHandle,
        });
      }
    );
  };

  /**
   * Resolves or rejects all queued promises waiting for token refresh
   * @param newToken The new token if refresh succeeded, or null/undefined if failed
   * @param error Error object if refresh failed
   */
  const processWaitListRefreshRequests = (
    newToken: string | null | undefined,
    error: unknown
  ) => {
    requestWaitListReferences.forEach((queueItem) => {
      if (newToken) {
        queueItem.resolve(newToken);
      } else {
        queueItem.reject(error);
      }
    });
    requestWaitListReferences = [];
  };

  /**
   * Redirects user to the login page
   * Called when authentication fails and cannot be recovered
   */
  const redirectLogin = () => {
    window.location.href = "/auth/logout?redirect=/login";
  };

  /**
   * Creates a custom HTTP error with status code
   * @param message Error message
   * @param statusCode HTTP status code
   * @returns HttpError object
   */
  const constructError = (
    message: string,
    statusCode: number
  ): HttpError => {
    const error = new Error(message) as HttpError;
    error.statusCode = statusCode;
    return error;
  };
  
  //Return functions that are needed to be used outside of this IIFE
  return {
    clearTokenData,
  }

})();

export default authTokenManager;

